import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DollarSign, Search, Loader2, AlertCircle, Download, FileBarChart, Settings } from 'lucide-react';
import { PageLayout } from '@/components/ui/PageLayout';
import { getCompensationReport } from '@/api/TournamentManagement/workPointApi';
import { getTournaments } from '@/api/TournamentManagement/tournamentApi';
import { getReferees } from '@/api/TournamentManagement/refereeApi';
import type { CompensationReportView, TournamentView, RefereeView } from '@/models/tournament';
import { useCurrency } from '@/hooks/useCurrency';
import CompensationReportTable from './components/CompensationReportTable';
import PayrollGenerationSection from './components/PayrollGenerationSection';

const Compensation: React.FC = () => {
  const { t } = useTranslation('tournament');
  const { format: formatCurrency } = useCurrency();

  // Pre-fill date range with current month
  const { defaultStart, defaultEnd } = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const today = now.toISOString().split('T')[0];
    return { defaultStart: startOfMonth, defaultEnd: today };
  }, []);

  // Filter state
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [tournamentId, setTournamentId] = useState('');
  const [refereeUserId, setRefereeUserId] = useState('');

  // Dropdown data
  const [tournaments, setTournaments] = useState<TournamentView[]>([]);
  const [referees, setReferees] = useState<RefereeView[]>([]);

  // Report state
  const [reportData, setReportData] = useState<CompensationReportView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch tournament and referee lists on mount
  useEffect(() => {
    getTournaments({ pageSize: 200 }).then((res) => {
      if (res.errors.length === 0 && res.payload) {
        setTournaments(res.payload.items);
      }
    }).catch(() => {});
    getReferees({ pageSize: 200 }).then((res) => {
      if (res.errors.length === 0 && res.payload) {
        setReferees(res.payload.items);
      }
    }).catch(() => {});
  }, []);

  const canGenerate = startDate && endDate;

  const handleGenerateReport = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError(null);
    setReportData(null);
    try {
      const result = await getCompensationReport({
        startDate,
        endDate,
        tournamentId: tournamentId ? Number(tournamentId) : undefined,
        refereeUserId: refereeUserId || undefined,
      });
      if (result.errors.length > 0) {
        setError(result.errors[0]?.description || t('Failed to generate report'));
      } else {
        setReportData(result.payload);
      }
    } catch {
      setError(t('Failed to generate report'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!reportData || reportData.lines.length === 0) return;
    const headers = [t('Referee'), t('Matches'), t('Total Work Points'), t('Total Currency Amount')];
    const rows = reportData.lines.map((line) => [
      `"${line.refereeName}"`,
      line.matchCount,
      line.totalWorkPoints,
      formatCurrency(line.totalCurrencyAmount),
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compensation-report-${startDate}-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const inputClasses =
    'w-full px-3 py-2 text-sm bg-background border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors outline-none';

  return (
    <PageLayout
      title={t('Compensation & Payroll')}
      description={t('View compensation reports and generate payroll entries for referees')}
      icon={DollarSign}
      accentColor="emerald"
    >
      {/* Filter Section */}
      <section
        className="bg-card border border-border rounded-lg p-6 mb-6"
        data-testid="compensation-filters"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('Filters')}
          </h3>
          <Link
            to="/tournament-management/work-points"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 hover:underline transition-colors"
            data-testid="work-point-settings-link"
          >
            <Settings size={14} />
            {t('View Work Point Settings')}
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('Start Date')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={inputClasses}
              data-testid="filter-start-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('End Date')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={inputClasses}
              data-testid="filter-end-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('Tournament')}
            </label>
            <select
              value={tournamentId}
              onChange={(e) => setTournamentId(e.target.value)}
              className={inputClasses}
              data-testid="filter-tournament"
            >
              <option value="">{t('All Tournaments')}</option>
              {tournaments.map((tour) => (
                <option key={tour.id} value={tour.id}>
                  {tour.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('Referee')}
            </label>
            <select
              value={refereeUserId}
              onChange={(e) => setRefereeUserId(e.target.value)}
              className={inputClasses}
              data-testid="filter-referee"
            >
              <option value="">{t('All Referees')}</option>
              {referees.map((ref) => (
                <option key={ref.userId} value={ref.userId}>
                  {ref.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleGenerateReport}
              disabled={!canGenerate || loading}
              className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-primary/20 justify-center"
              data-testid="generate-report-btn"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Search size={18} />
              )}
              {t('Generate Report')}
            </button>
            <button
              onClick={handleExportCsv}
              disabled={!reportData || reportData.lines.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-card border border-border rounded-lg hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="export-csv-btn"
            >
              <Download size={16} />
              {t('Export CSV')}
            </button>
          </div>
        </div>
      </section>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3" data-testid="compensation-error">
          <AlertCircle size={18} className="text-red-600 dark:text-red-400 shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="mb-6 flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-gray-500">
            <Loader2 size={24} className="animate-spin" />
            <span className="text-sm">{t('Loading data...')}</span>
          </div>
        </div>
      )}

      {/* First-Load Guidance */}
      {!reportData && !loading && !error && (
        <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="compensation-guidance">
          <FileBarChart size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('compensation.generateTitle')}
          </h3>
          <p className="text-muted-foreground mb-4 max-w-md mx-auto">
            {t('compensation.generateDescription')}
          </p>
        </div>
      )}

      {/* Report Table */}
      {reportData && !loading && (
        <div className="space-y-6">
          {/* Report Context Header */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t('compensation.reportDateRange', { start: startDate, end: endDate })}
            </p>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('Total Referees')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {reportData.lines.length}
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('Total Work Points')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {reportData.totalWorkPoints.toFixed(1)}
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('Total Currency Amount')}</p>
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                {formatCurrency(reportData.totalCurrencyAmount)}
              </p>
            </div>
          </div>

          {/* Table */}
          <CompensationReportTable data={reportData} startDate={startDate} endDate={endDate} />

          {/* Payroll Generation */}
          <PayrollGenerationSection
            reportData={reportData}
            startDate={startDate}
            endDate={endDate}
            tournamentId={tournamentId}
            refereeUserId={refereeUserId}
          />
        </div>
      )}
    </PageLayout>
  );
};

export default Compensation;
